import React, { FC } from 'react'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { BidResponse } from 'apps/fleet-planner/types/apiTypes'
import { formatWeight } from 'apps/fleet-planner/utils/marketplaceHelpers'
import { Bullet } from 'components/atoms'
import { LaneArrowIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'

export enum LOCATION_VARIANTS {
  BIDS_PAGE = 'bids',
  BIDS_MODAL = 'bidsModal',
}

interface ILocationProps {
  bid?: BidResponse
  variant?: LOCATION_VARIANTS
}

const useLocationStyles = makeStyles(() => ({
  info: {
    color: brandColors.info,
  },
  coolGray6: {
    color: brandColors.coolGray6,
  },
  coolGray5: {
    color: brandColors.coolGray5,
  },
  coolGray8: {
    color: brandColors.coolGray8,
  },
}))

export const Location: FC<ILocationProps> = ({ bid, variant }) => {
  const classes = useLocationStyles()
  const { shipment, status } = bid || {}
  const {
    deliveryCity,
    deliveryDate,
    deliveryStateCode,
    equipmentName,
    pickupCity,
    pickupDate,
    pickupStateCode,
    weight,
  } = shipment || {}
  const locationClassName = status === 'declined' ? classes.coolGray5 : classes.coolGray8
  const dateClassName = status === 'declined' ? classes.coolGray5 : classes.coolGray6
  const padding = variant === LOCATION_VARIANTS.BIDS_MODAL ? 0 : 0.5
  const arrowValue = variant === LOCATION_VARIANTS.BIDS_MODAL ? 'medium' : 'large'
  const arrowPos = arrowValue === 'large' ? '100%' : '18px'
  const variantSub1 = variant === LOCATION_VARIANTS.BIDS_MODAL ? 'caption' : 'subtitle1'
  const variantSub2 = variant === LOCATION_VARIANTS.BIDS_MODAL ? 'caption' : 'subtitle2'
  const deliveryCityStr =
    variant === LOCATION_VARIANTS.BIDS_MODAL ? capitalize(deliveryCity as string) : deliveryCity
  const pickupCityStr =
    variant === LOCATION_VARIANTS.BIDS_MODAL ? capitalize(pickupCity as string) : pickupCity
  const displayTimeZone = moment.tz.guess()

  return (
    <Box>
      <Box display='flex' alignItems='center' pb={padding}>
        {/* Pickup Location */}
        <Typography className={locationClassName} variant={variantSub1} noWrap>
          {`${pickupCityStr || ''}, ${pickupStateCode || ''}`}
        </Typography>
        {/* Arrow */}
        <Box className={locationClassName} mx={1} height={arrowPos}>
          <LaneArrowIcon className={arrowValue} />
        </Box>
        {/* Delivery Location */}
        <Typography className={locationClassName} variant={variantSub1} noWrap>
          {`${deliveryCityStr || ''}, ${deliveryStateCode || ''}`}
        </Typography>
      </Box>
      <Box>
        <Typography className={dateClassName} component='span' variant={variantSub2}>
          {`Pickup `}
          <FormattedDateTime
            datetime={pickupDate || ''}
            format='dateAndYearFullDateAlways'
            displayTimezone={displayTimeZone}
          />
        </Typography>{' '}
        <Bullet className={dateClassName} />
        <Typography className={dateClassName} component='span' variant={variantSub2}>
          {`Dropoff `}
          <FormattedDateTime
            datetime={deliveryDate || ''}
            format='dateAndYearFullDateAlways'
            displayTimezone={displayTimeZone}
          />
        </Typography>
        <Bullet className={dateClassName} />
        <Typography className={dateClassName} component='span' variant={variantSub2}>
          {equipmentName}
        </Typography>
        {!variant && (
          <>
            <Bullet className={dateClassName} />
            <Typography className={dateClassName} component='span' variant={variantSub2}>
              {formatWeight(weight || 0)}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  )
}
